import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FileText, Download, CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOC_IMAGE = "https://images.unsplash.com/photo-1758876020343-c8c2add9d527?crop=entropy&cs=srgb&fm=jpg";

const Documenti730 = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/documents/730`);
        setData(response.data);
      } catch (error) {
        console.error("Error fetching 730 documents:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative bg-slate-900 py-20 md:py-28">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${DOC_IMAGE})` }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm font-semibold tracking-[0.1em] uppercase text-slate-400 mb-4 block">
              Documenti Necessari
            </span>
            <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter text-white mb-6">
              {data?.title || "Modello 730"}
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Ecco l'elenco completo dei documenti necessari per la compilazione del Modello 730/{data?.year || "2026"}.
            </p>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="bg-red-50 border-b border-red-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-red-900 mb-1">Importante</h3>
              <p className="text-red-800">
                Porta con te tutti i documenti in originale o in copia leggibile. Se hai dubbi su un documento specifico, contattaci prima dell'appuntamento.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Document Categories */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {data?.categories?.map((category, idx) => (
                <div 
                  key={idx}
                  className="bg-white border border-slate-200 rounded-2xl p-8"
                  data-testid={`doc-category-${idx}`}
                >
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6 pb-4 border-b border-slate-200">
                    {category.name}
                  </h2>
                  <ul className="space-y-0">
                    {category.items.map((item, i) => (
                      <li 
                        key={i}
                        className="document-item flex items-start gap-3 py-4"
                      >
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-lg text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Quick Actions */}
                <div className="bg-slate-50 rounded-2xl p-6">
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">
                    Azioni Rapide
                  </h3>
                  <div className="space-y-3">
                    <Link to="/prenota" className="block">
                      <Button 
                        className="w-full bg-red-600 hover:bg-red-700 text-white rounded-full"
                        data-testid="doc730-prenota"
                      >
                        Prenota Appuntamento
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      className="w-full rounded-full"
                      onClick={() => window.print()}
                      data-testid="doc730-print"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Stampa Lista
                    </Button>
                  </div>
                </div>

                {/* Help Box */}
                <div className="bg-blue-900 text-white rounded-2xl p-6">
                  <h3 className="font-heading text-lg font-bold mb-3">
                    Hai bisogno di aiuto?
                  </h3>
                  <p className="text-blue-100 text-sm mb-4">
                    Se non sei sicuro di quali documenti portare, contattaci per una consulenza gratuita.
                  </p>
                  <Link to="/contatti">
                    <Button 
                      variant="outline" 
                      className="w-full bg-transparent border-white text-white hover:bg-white/10 rounded-full text-sm"
                      data-testid="doc730-contatti"
                    >
                      Contattaci
                    </Button>
                  </Link>
                </div>

                {/* ISEE Link */}
                <div className="bg-slate-100 rounded-2xl p-6">
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-3">
                    Ti serve anche l'ISEE?
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Consulta anche la lista dei documenti necessari per l'ISEE.
                  </p>
                  <Link to="/documenti-isee">
                    <Button 
                      variant="link" 
                      className="p-0 h-auto text-blue-900 font-semibold"
                      data-testid="doc730-isee-link"
                    >
                      Documenti ISEE
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mb-4">
            Pronto per il tuo 730?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Porta tutti i documenti e lascia fare a noi. Ti guideremo in ogni passaggio.
          </p>
          <Link to="/prenota">
            <Button 
              size="lg" 
              className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8"
              data-testid="doc730-cta-prenota"
            >
              Prenota Ora
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Documenti730;
